/**
 * Admin Overview Page
 *
 * Operational hub dashboard with:
 *   - Today's KPIs (new leads, deliveries, active contracts, routing success)
 *   - Pipeline status (secondary metrics)
 *   - Actionable alerts with links to relevant admin pages
 *   - Quick navigation grid with contextual mini-KPIs
 *   - Recent activity feed (leads + contracts)
 *
 * Design principle: Hub linking to specialized pages.
 * Does NOT include revenue/financial KPIs (reserved for TJ Command Center).
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useLeads, useLeadStats } from "@/hooks/useLeadsDatabase";
import { useRoutingAttemptStats } from "@/hooks/useRoutingAttempts";
import { useMockAgents } from "@/contexts/MockAgentsContext";
import { useContracts, computeContractMetrics } from "@/contexts/ContractsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Database,
  CheckCircle2,
  XCircle,
  Users,
  AlertTriangle,
  RefreshCw,
  Package,
  Activity,
  TrendingUp,
  Clock,
  FileText,
  Eye,
  BookOpen,
  Archive,
  DollarSign,
  Settings2,
  ScrollText,
  Headphones,
  HeartPulse,
  ArrowRight,
  PauseCircle,
  CalendarClock,
} from "lucide-react";

export default function AdminOverview() {
  const { data: leads } = useLeads();
  const { data: leadStats } = useLeadStats();
  const { data: attemptStats } = useRoutingAttemptStats();
  const { clientAgents } = useMockAgents();
  const { state: contractsState } = useContracts();

  // --- Derived metrics ---
  const totalLeads = leadStats?.total ?? 0;
  const routedCount = leadStats?.byStatus.ROUTED ?? 0;
  const newCount = leadStats?.byStatus.NEW ?? 0;
  const retryQueuedCount = leadStats?.byStatus.RETRY_QUEUED ?? 0;
  const unsoldCount = leadStats?.byStatus.UNSOLD ?? 0;
  const stockedCount = leadStats?.byStatus.STOCKED ?? 0;
  const reviewRequiredCount = leadStats?.byStatus.REVIEW_REQUIRED ?? 0;
  const _creditedCount = leadStats?.byStatus.CREDITED ?? 0;

  const routingSuccessRate = attemptStats
    ? (attemptStats.successRate * 100).toFixed(1)
    : "0.0";

  // Today's metrics
  const todayMetrics = useMemo(() => {
    if (!leads) return { newToday: 0, routedToday: 0 };
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const todayTs = startOfDay.getTime();
    const todayLeads = leads.filter(l => l.createdAt >= todayTs);
    return {
      newToday: todayLeads.length,
      routedToday: todayLeads.filter(l => l.status === "ROUTED").length,
    };
  }, [leads]);

  // Contract health
  const contractHealth = useMemo(() => {
    const contracts = contractsState.contracts;
    const active = contracts.filter(c => c.status === "active" || c.status === "at_risk");
    const paused = contracts.filter(c => c.status === "paused");
    const atRisk = contracts.filter(c => {
      if (c.status === "at_risk") return true;
      if (c.status === "active") return computeContractMetrics(c).isAtRisk;
      return false;
    });
    // Expiring within 7 days
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const expiringSoon = contracts.filter(c =>
      (c.status === "active" || c.status === "at_risk") &&
      c.cycleEnd > 0 &&
      c.cycleEnd - now < sevenDays &&
      c.cycleEnd > now
    );
    return {
      activeCount: active.length,
      pausedCount: paused.length,
      atRiskCount: atRisk.length,
      atRiskContracts: atRisk,
      expiringSoonCount: expiringSoon.length,
    };
  }, [contractsState.contracts]);

  // Agent stats
  const activeAgentCount = useMemo(() => clientAgents.filter(a => a.isActive).length, [clientAgents]);

  // Stuck leads (NEW for > 30 min)
  const stuckLeadsCount = useMemo(() => {
    if (!leads) return 0;
    const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
    return leads.filter(l => l.status === "NEW" && l.createdAt < thirtyMinAgo).length;
  }, [leads]);

  // Alerts
  const alerts = useMemo(() => {
    const items: { key: string; severity: "critical" | "warning" | "info"; icon: typeof AlertTriangle; title: string; description: string; link: string; linkLabel: string }[] = [];

    if (stuckLeadsCount > 0) {
      items.push({
        key: "stuck-leads",
        severity: "critical",
        icon: Clock,
        title: `${stuckLeadsCount} Lead${stuckLeadsCount > 1 ? "s" : ""} Stuck in NEW > 30min`,
        description: "Leads waiting for routing. Check agent availability and caps.",
        link: "/admin/lead-vault",
        linkLabel: "Open Lead Vault",
      });
    }

    if (contractHealth.atRiskCount > 0) {
      items.push({
        key: "at-risk-contracts",
        severity: "warning",
        icon: FileText,
        title: `${contractHealth.atRiskCount} Contract${contractHealth.atRiskCount > 1 ? "s" : ""} At Risk`,
        description: contractHealth.atRiskContracts.slice(0, 3).map(c => c.agentName || "Unassigned").join(", ") +
          (contractHealth.atRiskCount > 3 ? ` and ${contractHealth.atRiskCount - 3} more` : ""),
        link: "/admin/contracts",
        linkLabel: "View Contracts",
      });
    }

    if (contractHealth.expiringSoonCount > 0) {
      items.push({
        key: "expiring-contracts",
        severity: "warning",
        icon: CalendarClock,
        title: `${contractHealth.expiringSoonCount} Contract${contractHealth.expiringSoonCount > 1 ? "s" : ""} Expiring This Week`,
        description: "Contracts with cycle ending in the next 7 days.",
        link: "/admin/contracts",
        linkLabel: "View Contracts",
      });
    }

    if (retryQueuedCount > 5) {
      items.push({
        key: "retry-backlog",
        severity: "info",
        icon: RefreshCw,
        title: "Retry Backlog",
        description: `${retryQueuedCount} leads queued for retry.`,
        link: "/admin/lead-vault",
        linkLabel: "Open Lead Vault",
      });
    }

    if (reviewRequiredCount > 0) {
      items.push({
        key: "review-required",
        severity: "warning",
        icon: AlertTriangle,
        title: "Leads Need Review",
        description: `${reviewRequiredCount} lead(s) have validation errors requiring human review.`,
        link: "/admin/lead-vault",
        linkLabel: "Open Lead Vault",
      });
    }

    if (parseFloat(routingSuccessRate) < 50 && totalLeads > 0) {
      items.push({
        key: "low-success",
        severity: "critical",
        icon: XCircle,
        title: "Low Routing Success Rate",
        description: `Only ${routingSuccessRate}% of routing attempts succeeded. Check agent availability and caps.`,
        link: "/admin/routing-inspector",
        linkLabel: "Open Routing Inspector",
      });
    }

    if (contractHealth.pausedCount > 0) {
      items.push({
        key: "paused-contracts",
        severity: "info",
        icon: PauseCircle,
        title: `${contractHealth.pausedCount} Paused Contract${contractHealth.pausedCount > 1 ? "s" : ""}`,
        description: "Agents with delivery paused. Check for payment issues.",
        link: "/admin/contracts",
        linkLabel: "View Contracts",
      });
    }

    return items;
  }, [stuckLeadsCount, contractHealth, retryQueuedCount, reviewRequiredCount, routingSuccessRate, totalLeads]);

  // Recent leads (last 8)
  const recentLeads = useMemo(() => {
    if (!leads) return [];
    return leads.slice(0, 8);
  }, [leads]);

  // Quick nav items with contextual mini-KPIs
  const quickNav = useMemo(() => [
    {
      title: "Lead Vault",
      href: "/admin/lead-vault",
      icon: Archive,
      stat: newCount > 0 ? `${newCount} NEW` : `${totalLeads} total`,
      statColor: newCount > 0 ? "text-primary" : "text-muted-foreground",
    },
    {
      title: "Contracts",
      href: "/admin/contracts",
      icon: FileText,
      stat: `${contractHealth.activeCount} active`,
      statColor: contractHealth.atRiskCount > 0 ? "text-orange-600" : "text-muted-foreground",
    },
    {
      title: "Agents",
      href: "/admin/agents",
      icon: Users,
      stat: `${activeAgentCount} active`,
      statColor: "text-muted-foreground",
    },
    {
      title: "Routing Inspector",
      href: "/admin/routing-inspector",
      icon: Eye,
      stat: `${routingSuccessRate}% success`,
      statColor: parseFloat(routingSuccessRate) < 50 ? "text-destructive" : "text-muted-foreground",
    },
    {
      title: "Delivery Ledger",
      href: "/admin/delivery-ledger",
      icon: BookOpen,
      stat: `${routedCount} routed`,
      statColor: "text-muted-foreground",
    },
    {
      title: "Client Health",
      href: "/admin/client-health",
      icon: HeartPulse,
      stat: "Scores & trends",
      statColor: "text-muted-foreground",
    },
    {
      title: "Floor Pricing",
      href: "/admin/pricing",
      icon: DollarSign,
      stat: "Rules config",
      statColor: "text-muted-foreground",
    },
    {
      title: "Account Managers",
      href: "/admin/account-managers",
      icon: Headphones,
      stat: "Assignments",
      statColor: "text-muted-foreground",
    },
    {
      title: "System",
      href: "/admin/system",
      icon: Settings2,
      stat: "Health & diagnostics",
      statColor: "text-muted-foreground",
    },
    {
      title: "Logs",
      href: "/admin/logs",
      icon: ScrollText,
      stat: "Activity trail",
      statColor: "text-muted-foreground",
    },
  ], [newCount, totalLeads, contractHealth, activeAgentCount, routingSuccessRate, routedCount]);

  const severityStyles = {
    critical: "bg-destructive/10 border-destructive/20",
    warning: "bg-orange-500/10 border-orange-500/20",
    info: "bg-blue-500/10 border-blue-500/20",
  };
  const severityIconColor = {
    critical: "text-destructive",
    warning: "text-orange-600",
    info: "text-blue-600",
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Today's snapshot &bull; {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
        </div>

        {/* Today's KPIs */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Leads Today</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{todayMetrics.newToday}</div>
              <p className="text-xs text-muted-foreground">{totalLeads} total all-time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Delivered Today</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{todayMetrics.routedToday}</div>
              <p className="text-xs text-muted-foreground">{routedCount} total routed</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{contractHealth.activeCount}</div>
              {contractHealth.atRiskCount > 0 ? (
                <p className="text-xs text-orange-600 font-medium">{contractHealth.atRiskCount} at risk</p>
              ) : (
                <p className="text-xs text-muted-foreground">All on track</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{routingSuccessRate}%</div>
              <p className="text-xs text-muted-foreground">
                {attemptStats?.total ?? 0} attempts
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Status */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-xl font-bold">{activeAgentCount}</span>
              </div>
              <p className="text-xs text-muted-foreground">Active Agents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-blue-600" />
                <span className="text-xl font-bold text-blue-600">{retryQueuedCount}</span>
              </div>
              <p className="text-xs text-muted-foreground">Retry Queue</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-xl font-bold">{stockedCount}</span>
              </div>
              <p className="text-xs text-muted-foreground">Stocked</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="text-xl font-bold text-destructive">{unsoldCount}</span>
              </div>
              <p className="text-xs text-muted-foreground">Unsold</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-xl font-bold">
                  {attemptStats?.averageDurationMs?.toFixed(0) ?? '—'}ms
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Avg Route Time</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts & Action Items */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className={`h-4 w-4 ${alerts.length > 0 ? 'text-amber-500' : 'text-green-500'}`} />
              Alerts{alerts.length > 0 ? ` (${alerts.length})` : ""}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.length === 0 ? (
                <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">All Systems Normal</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      No issues detected. Routing is operating within expected parameters.
                    </p>
                  </div>
                </div>
              ) : (
                alerts.map((alert) => (
                  <div key={alert.key} className={`flex items-start gap-3 p-3 rounded-lg border ${severityStyles[alert.severity]}`}>
                    <alert.icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${severityIconColor[alert.severity]}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{alert.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
                    </div>
                    <Link
                      to={alert.link}
                      className="flex items-center gap-1 text-xs font-medium text-primary hover:underline flex-shrink-0 mt-0.5"
                    >
                      {alert.linkLabel}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Navigation Grid */}
        <div>
          <h2 className="text-sm font-semibold text-foreground mb-3">Quick Navigation</h2>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {quickNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="group"
              >
                <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-primary/30 group-hover:bg-accent/5">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <item.icon className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium text-foreground">{item.title}</span>
                    </div>
                    <p className={`text-xs ${item.statColor}`}>{item.stat}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Leads Feed */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-4 w-4" />
                Recent Leads
              </CardTitle>
              <Link
                to="/admin/lead-vault"
                className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                View all
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No leads yet.
              </p>
            ) : (
              <div className="space-y-1">
                {recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between py-2.5 border-b last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      {lead.status === "ROUTED" ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : lead.status === "NEW" ? (
                        <Database className="h-4 w-4 text-primary flex-shrink-0" />
                      ) : lead.status === "RETRY_QUEUED" ? (
                        <RefreshCw className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      ) : lead.status === "STOCKED" ? (
                        <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      ) : lead.status === "CREDITED" ? (
                        <XCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{lead.leadTypeName}</p>
                        <p className="text-xs text-muted-foreground">
                          {lead.state} &bull; {new Date(lead.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={lead.status === "ROUTED" ? "default" :
                               lead.status === "NEW" ? "outline" :
                               lead.status === "RETRY_QUEUED" ? "outline" :
                               lead.status === "CREDITED" ? "outline" : "destructive"}
                      className={`text-xs ${lead.status === "CREDITED" ? "border-amber-500 text-amber-700" : ""}`}
                    >
                      {lead.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
